import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CdwOuCollectionEditComponent } from '@cdw-ae/cdw-ou-collection-edit';
import {
  AccessManagementOrgUnitCollectionOURecord,
  AccessManagementOrgUnitCollectionRecord,
} from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { CdwConfirmationDialogComponent, CdwConfirmationDialogData, CdwDialogModule, ConfirmationDialogResult } from '@cdw-ae/common-ui-components-dialog';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { firstValueFrom, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { OrgUnitsDataService } from '../../org-units/org-units-data.service';
import { OrgUnitCollectionsDataService } from '../ou-collections-data.service';

@Component({
  selector: 'ou-collection-edit',
  templateUrl: './ou-collection-edit.component.html',
  styleUrls: ['./ou-collection-edit.component.scss'],
  imports: [CdwOuCollectionEditComponent, CommonModule, CdwDialogModule],
})
export class OuCollectionEditComponent implements OnInit, OnDestroy {
  @ViewChild('ouCollectionEdit')
  public orgUnitCollectionEditComponent!: CdwOuCollectionEditComponent;
  public currentOrgUnitCollectionOUs!: AccessManagementOrgUnitCollectionOURecord[];
  public currentOrgUnitCollection!: AccessManagementOrgUnitCollectionRecord;
  public selectedOrgUnitCollectionId!: number | 'new';
  public isLoading = false;
  public orgUnitSelectOptions!: TreeNode[];
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private orgUnitCollectionService: OrgUnitCollectionsDataService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private orgUnitsDataService: OrgUnitsDataService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;

    // Request hierarchical structure with parent/child relationships
    // This allows the filtered-tree-picker to display OUs in a tree view with checkboxes
    this.orgUnitSelectOptions = await firstValueFrom(
      this.orgUnitsDataService.getOrgUnits()
    );

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((paramMap: ParamMap): void => {
        const idParam = paramMap.get('id');
        if (idParam && idParam !== 'new') {
          this.selectedOrgUnitCollectionId = parseInt(idParam, 10);

          this.orgUnitCollectionService
            .getOrgUnitCollectionById(this.selectedOrgUnitCollectionId)
            .pipe(take(1))
            .subscribe((response) => {
              this.currentOrgUnitCollectionOUs =
                response.data.ouCollectionOus;
              this.currentOrgUnitCollection =
                response.data;
            });
        } else {
          if (this.currentOrgUnitCollection) {
            this.orgUnitCollectionEditComponent.ouCollection = undefined;
            this.orgUnitCollectionEditComponent.ouCollectionOus = [];
            this.orgUnitCollectionEditComponent.OuCollectionForm.reset();
          }
          this.selectedOrgUnitCollectionId = 'new';
        }
        this.isLoading = false;
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public async onSubmitEdit(event: Event): Promise<void> {
    const orgUnitCollectionData = event as unknown as {
      name: string;
      description: string;
      ous: string[];
    };

    const payload = {
      name: orgUnitCollectionData.name,
      description: orgUnitCollectionData.description,
    };

    // Convert OU paths from tree picker to GroupingAsset UUIDs for the backend
    const orgUnitUuids = orgUnitCollectionData.ous
      .map(path => this.orgUnitsDataService.pathToUuidMap.get(path))
      .filter((uuid): uuid is string => uuid !== undefined);

    // Check for duplicate OU collection name (case-insensitive)
    const existingCollectionsResponse = await firstValueFrom(
      this.orgUnitCollectionService.getOrgUnitCollections()
    );

    if (existingCollectionsResponse?.data && Array.isArray(existingCollectionsResponse.data)) {
      const duplicateCollection = existingCollectionsResponse.data.find(
        (collection) =>
          collection.name.toLowerCase() === orgUnitCollectionData.name.toLowerCase() &&
          collection.id !== this.selectedOrgUnitCollectionId
      );

      if (duplicateCollection) {
        this.snackbar.openFromComponent(ErrorSnackbarComponent, {
          data: `An OU collection with the name "${orgUnitCollectionData.name}" already exists. Please choose a different name.`,
        });
        return;
      }
    }

    if (
      this.selectedOrgUnitCollectionId &&
      this.selectedOrgUnitCollectionId !== 'new'
    ) {
      const observable =
        await this.orgUnitCollectionService.updateOrgUnitCollection(
          payload,
          orgUnitUuids,
          this.selectedOrgUnitCollectionId
        );

      observable.pipe(take(1)).subscribe(async (response) => {
        if (response.success) {
          this.snackbar.open('OU collection updated successfully.', 'OK', { duration: 6000 });
          // Mark form as pristine to prevent "unsaved changes" dialog on navigation
          this.orgUnitCollectionEditComponent.OuCollectionForm.markAsPristine();
          await this.router.navigate(['/administration/access-management']);
        } else {
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: 'An unexpected error occurred trying to update the OU collection',
          });
        }
      });
    } else {
      const observable =
        await this.orgUnitCollectionService.addOrgUnitCollection(
          payload,
          orgUnitUuids
        );

      observable.pipe(take(1)).subscribe(async (response) => {
        if (response.success) {
          this.snackbar.open('OU collection created successfully.', 'OK', { duration: 6000 });
          // Mark form as pristine to prevent "unsaved changes" dialog on navigation
          this.orgUnitCollectionEditComponent.OuCollectionForm.markAsPristine();
          await this.router.navigate(['/administration/access-management']);
        } else {
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: 'An unexpected error occurred trying to create the OU collection',
          });
        }
      });
    }
  }

  public async onCancelEdit(): Promise<void> {
    await this.router.navigate(['/administration/access-management']);
  }

  public onDeleteOrgUnitCollection(): void {
    if (this.selectedOrgUnitCollectionId === 'new') {
      return;
    }

    // Check if OU collection can be deleted before showing confirmation dialog
    this.orgUnitCollectionService.canDeleteOrgUnitCollection(this.selectedOrgUnitCollectionId)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: canDeleteResponse.message || 'Error deleting OU collection, it is assigned to a designation',
          });
          return;
        }

        // OU collection can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
          data: {
            title: 'Confirmation',
            message: 'Are you sure you want to delete this OU collection?',
            type: 'warning',
            layout: 'warning-box',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmButtonColor: 'warn',
            hard: true,
          },
          width: '540px',
          maxWidth: '100%',
          disableClose: true,
          hasBackdrop: true,
        });

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined) => {
            if (
              result?.confirmed &&
              this.selectedOrgUnitCollectionId &&
              this.selectedOrgUnitCollectionId !== 'new'
            ) {
              const observable =
                await this.orgUnitCollectionService.deleteOrgUnitCollection(
                  this.selectedOrgUnitCollectionId
                );

              observable.pipe(take(1)).subscribe(async (response) => {
                if (response.success) {
                  this.snackbar.open('OU collection deleted successfully.', 'OK', { duration: 6000 });
                  await this.router.navigate(['/administration/access-management']);
                } else {
                  this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                    data: 'An error occurred while deleting the OU collection.',
                  });
                }
              });
            }
          });
      });
  }

  public getTitleText(): string {
    if (
      this.selectedOrgUnitCollectionId !== 'new' &&
      this.currentOrgUnitCollection
    ) {
      return `Edit OU Collection: ${this.currentOrgUnitCollection.name}`;
    }
    return 'Add New OU Collection';
  }

  /**
   * Confirms navigation when user has unsaved changes
   * Shows a warning dialog to prevent accidental data loss
   * @returns Observable<boolean> - true if navigation allowed, false if blocked
   */
  confirmNavigationWithUnsavedChanges(): Observable<boolean> | boolean {
    // Check if form is dirty (has unsaved changes)
    if (!this.orgUnitCollectionEditComponent?.OuCollectionForm?.dirty) {
      return true;
    }

    // Show confirmation dialog to warn user about data loss
    const dialogData: CdwConfirmationDialogData = {
      title: 'Confirmation',
      message:
        'You have unsaved changes. Are you sure you would like to continue?',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Leave Page',
      cancelText: 'Stay on Page',
      confirmButtonColor: 'warn',
      showCancelButton: true,
      hard: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true, // Prevent Escape key and backdrop click from closing
    });

    // Return observable that resolves based on user's choice
    return dialogRef.afterClosed().pipe(
      map((result: { confirmed: boolean; dontShowAgain?: boolean }) => {
        return result?.confirmed ?? false;
      })
    );
  }
}
